/**
 * Defensive ability states that read (plan 29, lane BS 2026-10-11).
 *
 * Lane BR made Shield Wall, Deterrence and Cloak of Shadows real server
 * statuses (statusApplied → StatusVFXHandler for every viewer), but the
 * status resolved to each ability's CAST burst — a one-shot that fades in a
 * moment — so the state was nearly invisible for its whole duration. These
 * are persistent states drawn around the body centre (entityVfxAnchor),
 * mapped in STATUS_PRESENTATIONS. Pixi 8: every shape is drawn once; the
 * only animation is an alpha/scale breath on the outer shape, and reduced
 * motion keeps the static read.
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';

/** Shield Wall: a steel tower shield on the facing side and a pale ward dome. */
const SHIELD_WALL_STATE: VFXEffectConfig = {
  id: 'shield_wall_state',
  name: 'Shield Wall State',
  category: 'status',
  description: 'A steel ward dome around the body with a bright rim: damage is cut while it stands',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 92, height: 98, color: 0x9fb4c8, alpha: 0.18, depth: 99,
      stroke: { color: 0xdfe8f2, width: 3 },
      tween: { alpha: 0.3, scale: 1.03, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'polygon', polygonPoints: [-10, -22, 10, -22, 12, 8, 0, 24, -12, 8], offsetX: 34,
      color: 0x8fa3b8, alpha: 0.85, depth: 101, stroke: { color: 0xf2f6fa, width: 2 } },
    { type: 'rect', width: 3, height: 34, offsetX: 34, offsetY: 0, color: 0xf2f6fa, alpha: 0.9, depth: 102 },
  ],
};

/** Deterrence: turning arcs of light that deflect — immune while they spin. */
const DETERRENCE_STATE: VFXEffectConfig = {
  id: 'deterrence_state',
  name: 'Deterrence State',
  category: 'status',
  description: 'Three bright deflecting arcs circle the body: every attack is turned aside',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 96, height: 96, color: 0x88ccff, alpha: 0.12, depth: 99,
      stroke: { color: 0xbfe6ff, width: 2 } },
    ...[0, 120, 240].map((deg) => ({
      type: 'arc' as const, arcRadius: 50, arcStartAngle: deg - 35, arcEndAngle: deg + 35,
      color: 0xe6f6ff, alpha: 0.95, depth: 101, stroke: { color: 0xe6f6ff, width: 4 },
      tween: { rotation: Math.PI * 2, duration: 1600, repeat: -1, ease: 'Linear' },
    })),
  ],
};

/** Cloak of Shadows: a dark shroud with a violet rim — magic slides off. */
const CLOAK_OF_SHADOWS_STATE: VFXEffectConfig = {
  id: 'cloak_of_shadows_state',
  name: 'Cloak of Shadows State',
  category: 'status',
  description: 'A dark violet shroud wraps the body: immune to magic, physical blows blunted',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 86, height: 100, color: 0x2a0a3a, alpha: 0.42, depth: 99,
      stroke: { color: 0xb36bff, width: 3 },
      tween: { alpha: 0.55, scale: 1.04, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 6, color: 0xd9b3ff, alpha: 0.9, depth: 101,
      orbit: { radius: 44, speed: 0.8 } },
  ],
};

export const DEFENSIVE_STATE_VFX: VFXEffectConfig[] = [SHIELD_WALL_STATE, DETERRENCE_STATE, CLOAK_OF_SHADOWS_STATE];
